<script setup lang="ts">
import TeamStats from "~/components/TeamStats.vue";
import type {Trail} from "~/types/Trail";
import type {SerializeObject} from "nitropack";
import type {GameStatsModel} from "~/types/TeamStatsData";

const {t, locale} = useI18n()

const seoTitle = computed(() => t('seoTitle', {city: data?.value?.city.english}))
const seoDescription = computed(() => t('seoDescription', {city: data?.value?.city.english}))

const route = useRoute();
const token = typeof route.query.token === "string" ? route.query.token : "";

const unlockedTokens = useCookie<string[]>('unlockedTokens', {default: () => []});
const {data: supportAccess} = await useFetch<{allowsToken: boolean}>("/api/support/session", {
  query: {token},
});
const needsEmailVerification = ref<boolean>(
  !unlockedTokens.value.includes(token) && !supportAccess.value?.allowsToken,
);
const {error, data: data,} = await useFetch<GameStatsModel>(`/api/profile?token=${token}`);

function unlockStats(): void {
  if (!unlockedTokens.value.includes(token)) {
    unlockedTokens.value = [...unlockedTokens.value, token];
  }
  needsEmailVerification.value = false;
}

const nbTrails = ref<SerializeObject<Trail>[] | null>();

async function fetchNearbyTrails(trailIdentifier: string) {
  const {data: d} = await useFetch(`/api/nearby/${trailIdentifier}?lang=${locale.value}`);
  nbTrails.value = d.value as SerializeObject<Trail>[] | null;
}

if (!error.value) {
  if (data?.value?.trailIdentifier) {
    await fetchNearbyTrails(data.value.trailIdentifier);
  }
}

watch(locale, async () => {
  if (data?.value?.trailIdentifier) {
    await fetchNearbyTrails(data.value.trailIdentifier);
  }
});

// Team disclosure state: each team is a collapsible card. With 1-2 teams they start open.
const openTeams = ref<Set<number>>(new Set());
if (data.value?.teams && data.value.teams.length <= 2) {
  openTeams.value = new Set(data.value.teams.map((t: any) => t.id));
}
function toggleTeam(id: number) {
  const next = new Set(openTeams.value);
  next.has(id) ? next.delete(id) : next.add(id);
  openTeams.value = next;
}
const isTeamOpen = (id: number) => openTeams.value.has(id);

useSeoMeta({
  title: seoTitle.value,
  ogTitle: seoTitle.value,
  description: seoDescription.value,
  ogDescription: seoDescription.value,
  ogImage: data?.value?.teams?.[0]?.groupPictureThumbUrl || '/Explorial_Logo.svg',
});
</script>

<template>
  <LockedOverlay v-if="!error && data" :is-locked="needsEmailVerification" :gameCode="data.gameCode"
                 :trail="data.trailIdentifier" @unlock="unlockStats">
    <div v-if="data.teams && Array.isArray(data.teams)" class="space-y-12 py-8">
      <!-- Hero -->
      <header class="text-center">
        <div class="relative h-52">
          <ClientOnly>
            <Vue3Lottie class="abs-center z-10" animationLink="./Animation - 1718959358212_v2.json" :height="200"
                        :width="200" :loop="false"/>
            <Vue3Lottie class="abs-center z-0" animationLink="./Animation - 1719320637460.json" :height="200"
                        :width="200"/>
          </ClientOnly>
        </div>
        <h1 class="text-3xl font-extrabold text-stone-900">{{ $t("mainTitle", {city: data.city.english}) }}</h1>
        <p class="mx-auto mt-3 max-w-md leading-relaxed text-stone-500">{{ $t("mainDescription", {city: data.city.english}) }}</p>
      </header>

      <FomoCouponTimer/>

      <div class="space-y-3">
        <div
          v-for="team in data.teams"
          :key="team.id"
          class="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-soft">
          <button
            type="button"
            class="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-stone-50"
            @click="toggleTeam(team.id)">
            <span class="font-bold text-stone-900">{{ $t('teamStatsTitle', { teamName: team.name }) }}</span>
            <UIcon
              name="i-heroicons-chevron-down-20-solid"
              :class="['h-5 w-5 shrink-0 text-stone-400 transition-transform duration-200', isTeamOpen(team.id) ? 'rotate-180' : '']" />
          </button>
          <div v-if="isTeamOpen(team.id)" class="border-t border-stone-200 bg-stone-50 px-4 py-6 sm:px-5">
            <TeamStats :team="team" :game="data"/>
          </div>
        </div>
      </div>

      <FomoCouponTimer/>

      <TripAdvisorReviewSection v-if="data.tripAdvisorLink" :link="data.tripAdvisorLink"/>

      <StatisticsComponent v-if="nbTrails" :title="$t('moreTrailsTitle')"
                           :description="$t('moreTrailsDescription')"
                           :elements="nbTrails.map(t => ({ title: t.cityName, description: t.cityNameSuffix, image: t.image.url, href: t.shareUrl }))"
                           :columns="2" :animate="true">
        <div class="mt-12 text-center">
          <h2 class="text-xl font-extrabold text-stone-900">{{ $t('moreTrailsThankYou') }}</h2>
          <p class="mt-2 leading-relaxed text-stone-500">{{ $t('moreTrailThankYou2') }}</p>
          <p class="leading-relaxed text-stone-500">{{ $t('moreTrailThankYou3') }}</p>
          <UButton block size="lg" class="mt-6 justify-center" :to="$t('shopUrl')" target="_blank">{{ $t('moreTrailsButton') }}</UButton>
        </div>
      </StatisticsComponent>
    </div>
  </LockedOverlay>

  <TripAdvisorBanner
      v-if="!error && data && data.tripAdvisorLink && !needsEmailVerification"
      :link="data.tripAdvisorLink"/>

  <div v-if="error" class="py-24 text-center">
    <h1 class="text-2xl font-extrabold text-stone-900">{{ $t('errorOops') }}</h1>
    <p v-if="error.statusCode == 500" class="mt-3 text-stone-500">{{ $t('errorFailed') }}</p>
    <p v-if="error.statusCode == 404" class="mt-3 text-stone-500">{{ $t('errorNotFound') }}</p>
  </div>

</template>

<style scoped>
.abs-center {
  position: absolute;
  top: 7rem;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
